import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { trackFailedLogin } from '../../../utils/notificationStore';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return 'E-postadresse er påkrevd';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) return 'Ugyldig e-postadresse (f.eks. navn@eksempel.no)';
        return '';
      case 'password':
        if (!value) return 'Passord er påkrevd';
        if (value?.length < 6) return 'Passord må være minst 6 tegn';
        return '';
      default:
        return '';
    }
  };

  const validate = () => {
    const newErrors = {};
    ['email', 'password']?.forEach(field => {
      const err = validateField(field, formData?.[field]);
      if (err) newErrors[field] = err;
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched?.[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
    if (authError) setAuthError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ email: true, password: true });
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 800));
    const users = JSON.parse(localStorage.getItem('veiassistent_users') || '[]');
    const user = users?.find(u => u?.email === formData?.email && u?.password === formData?.password);
    if (user) {
      const session = { id: user?.id, email: user?.email, name: user?.name, loggedInAt: new Date()?.toISOString() };
      localStorage.setItem('veiassistent_session', JSON.stringify(session));
      setIsLoading(false);
      navigate('/dashboard');
    } else {
      setIsLoading(false);
      trackFailedLogin(formData?.email);
      setAuthError('Ugyldig e-post eller passord. Sjekk opplysningene og prøv igjen.');
    }
  };

  const errorMsgStyle = {
    fontFamily: 'var(--font-caption)',
    fontSize: '12px',
    fontWeight: 400,
    color: '#FF5722',
    marginTop: '6px',
    letterSpacing: '0.01em',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full">
      {/* Auth Error */}
      {authError && (
        <div
          className="mb-6 px-4 py-3 border-l-4"
          style={{
            background: 'rgba(255, 87, 34, 0.08)',
            borderLeftColor: '#FF5722',
            borderTop: '1px solid rgba(255, 87, 34, 0.2)',
            borderRight: '1px solid rgba(255, 87, 34, 0.2)',
            borderBottom: '1px solid rgba(255, 87, 34, 0.2)',
          }}
          role="alert"
        >
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', fontWeight: 400, lineHeight: 1.5, color: '#FF5722', letterSpacing: '0.01em' }}>
            {authError}
          </p>
        </div>
      )}
      {/* Email Field */}
      <div className="mb-6">
        <label
          htmlFor="email"
          style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', display: 'block', marginBottom: '8px' }}
        >
          E-postadresse *
        </label>
        <div className="relative">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData?.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="navn@eksempel.no"
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              borderBottom: errors?.email ? '2px solid #FF5722' : '2px solid rgba(156, 163, 175, 0.3)',
              borderRadius: 0,
              padding: '10px 0',
              fontFamily: 'var(--font-body)',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'var(--color-foreground)',
              outline: 'none',
              transition: 'border-color 250ms ease-out',
              letterSpacing: '0.01em',
            }}
            onFocus={e => { if (!errors?.email) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
          />
        </div>
        {errors?.email && (
          <p style={errorMsgStyle}><span>⚠</span>{errors?.email}</p>
        )}
      </div>
      {/* Password Field */}
      <div className="mb-2">
        <label
          htmlFor="password"
          style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', display: 'block', marginBottom: '8px' }}
        >
          Passord *
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          value={formData?.password}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Skriv inn passord"
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            borderBottom: errors?.password ? '2px solid #FF5722' : '2px solid rgba(156, 163, 175, 0.3)',
            borderRadius: 0,
            padding: '10px 0',
            fontFamily: 'var(--font-body)',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.5,
            color: 'var(--color-foreground)',
            outline: 'none',
            transition: 'border-color 250ms ease-out',
            letterSpacing: '0.01em',
          }}
          onFocus={e => { if (!errors?.password) e.target.style.borderBottomColor = 'var(--color-primary)'; }}
        />
        {errors?.password && (
          <p style={errorMsgStyle}><span>⚠</span>{errors?.password}</p>
        )}
      </div>
      {/* Forgot Password */}
      <div className="flex justify-end mb-8">
        <Link
          to="/account-recovery"
          style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', fontWeight: 500, color: 'var(--color-primary)', textDecoration: 'none', letterSpacing: '0.01em', transition: 'opacity 150ms ease-out' }}
          onMouseEnter={e => e.target.style.opacity = '0.75'}
          onMouseLeave={e => e.target.style.opacity = '1'}
        >
          Glemt passord?
        </Link>
      </div>
      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        style={{
          width: '100%',
          background: isLoading ? 'rgba(255, 87, 34, 0.6)' : 'var(--color-accent)',
          border: '2px solid var(--color-accent)',
          borderRadius: 'var(--radius-sm)',
          padding: '14px 24px',
          fontFamily: 'var(--font-heading)',
          fontSize: '14px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#FFFFFF',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          transition: 'all 250ms ease-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
        onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.background = '#E64A19'; e.currentTarget.style.borderColor = '#E64A19'; } }}
        onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.borderColor = 'var(--color-accent)'; } }}
      >
        {isLoading ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            Logger inn...
          </>
        ) : 'Logg inn'}
      </button>
      {/* Register Link */}
      <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', textAlign: 'center', marginTop: '24px', lineHeight: 1.5 }}>
        Har du ikke konto?{' '}
        <Link
          to="/register"
          style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 500, color: 'var(--color-primary)', textDecoration: 'none', transition: 'opacity 150ms ease-out' }}
          onMouseEnter={e => e.target.style.opacity = '0.75'}
          onMouseLeave={e => e.target.style.opacity = '1'}
        >
          Registrer deg
        </Link>
      </p>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(156, 163, 175, 0.5); }
      `}</style>
    </form>
  );
};

export default LoginForm;
